import DocsLayout from '@/components/docs/layout'
import { createFileRoute } from '@tanstack/react-router'
import { DocsCallout, DocsInlineCode, DocsLink, DocsP, DocsSection, DocsTitle, DocsChecklist, DocsChecklistItem } from '@/components/docs/markdown'
import { buildHead } from '@/lib/utils'
import { GITHUB_REPO_URL, OWNER_URL, APP_URL } from '@/lib/constants'

const title = 'Privacy Policy - Honeypot'
const description = 'Official Honeypot bot privacy policy.'
const url = `${APP_URL}/docs/legal/privacy`

export const Route = createFileRoute('/docs/legal/privacy')({
  component: RouteComponent,
  head: () => buildHead({ title, description, url }),
})

function RouteComponent() {
  return <DocsLayout>
    <DocsTitle title="Privacy Policy" description="Official Honeypot bot privacy policy." />

    <DocsCallout tone="note" title="Disclaimer">
      <DocsP>This page is a presentation-friendly edition of the canonical legal text. The authoritative source is available at <DocsLink href={`${GITHUB_REPO_URL}/blob/main/legal.md`}>github.com/RiskyMH/Honeypot</DocsLink>.</DocsP>
    </DocsCallout>


    <DocsSection title="Privacy Policy">
      <DocsP><strong>Last Updated: February 23, 2026</strong></DocsP>
      <DocsP><DocsLink href={OWNER_URL}>RiskyMH</DocsLink> operates the Honeypot Discord bot. This Privacy Policy explains exactly what data the bot collects, stores, and uses.</DocsP>
    </DocsSection>

    <DocsSection title="Information We Collect">
      <DocsP>The bot stores only the minimum data needed to function in a local SQLite database:</DocsP>
      <DocsChecklist>
        <DocsChecklistItem>Server ID, honeypot channel ID, warning message ID, optional log channel ID, action setting ('softban', 'ban', or 'disabled'), and enabled experimental features ('channel-warmer', 'random-channel-name', etc.) for each server it is in</DocsChecklistItem>
        <DocsChecklistItem>Server ID, user ID, and timestamp for each incident where a user triggers the honeypot (counter/stat purposes)</DocsChecklistItem>
        <DocsChecklistItem>Custom messages set by server administrators (honeypot warning, DM, and log messages), if provided</DocsChecklistItem>
      </DocsChecklist>
    </DocsSection>

    <DocsSection title="How We Use Your Information">
      <DocsP>This data powers the bot's core functions:</DocsP>
      <DocsChecklist>
        <DocsChecklistItem>Monitor the designated honeypot channel</DocsChecklistItem>
        <DocsChecklistItem>Execute the configured moderation action (ban, softban, or disabled)</DocsChecklistItem>
        <DocsChecklistItem>Update the warning message counter</DocsChecklistItem>
        <DocsChecklistItem>Log events to the configured channel when set</DocsChecklistItem>
        <DocsChecklistItem>Send custom warning, DM, and log messages as configured</DocsChecklistItem>
      </DocsChecklist>
    </DocsSection>

    <DocsSection title="Data Storage and Retention">
      <DocsP>Data is stored in a single local SQLite file. All server data is automatically deleted when the bot is removed from a server. No backups or cloud storage are used.</DocsP>
    </DocsSection>

    <DocsSection title="What We Do Not Collect">
      <DocsChecklist>
        <DocsChecklistItem>Message contents</DocsChecklistItem>
        <DocsChecklistItem>Full member lists</DocsChecklistItem>
        <DocsChecklistItem>Activity outside the honeypot channel</DocsChecklistItem>
        <DocsChecklistItem>Private messages or voice data</DocsChecklistItem>
        <DocsChecklistItem>IP addresses or account details</DocsChecklistItem>
        <DocsChecklistItem>File attachments</DocsChecklistItem>
      </DocsChecklist>
      <DocsP>No usernames, nicknames, message contents, Discord IDs, or private Discord data are ever stored. Any details fetched from Discord’s API (like usernames or server names) are used in memory only, and never written to disk.</DocsP>
      <DocsP>Logs generated by Honeypot do not contain Discord IDs, usernames, or any personal information linking actions to specific individuals. Logs only record aggregate statistics (such as number of bans, failures, or moderation events) and anonymous event stages for troubleshooting or statistics purposes. All logging is intentionally designed to preserve privacy and anonymity of both moderators and members.</DocsP>
    </DocsSection>

    <DocsSection title="Third-Party Sharing">
      <DocsP>No data is shared with any third parties. All processing happens through Discord's API.</DocsP>
    </DocsSection>

    <DocsSection title="Discord's Role">
      <DocsP>The bot uses Discord's API. <DocsLink href="https://discord.com/privacy">Discord's Privacy Policy</DocsLink> applies to their platform data.</DocsP>
    </DocsSection>

    <DocsSection title="Your Rights">
      <DocsP>Server owners can remove the bot at any time, which automatically deletes all associated data. Settings are configurable via the <DocsInlineCode>/honeypot</DocsInlineCode> command.</DocsP>
    </DocsSection>
  </DocsLayout>
}
